import { api } from "../api";
import { CardBrand, CardKind, CardOffer, CardStatus, UserCard } from "./mocks";

const BRAND_MAP: Record<string, CardBrand> = {
  visa: "visa",
  mastercard: "mastercard",
  unionpay: "unionpay",
};

const STATUS_MAP: Record<string, CardStatus> = {
  ACTIVE: "active",
  PROCESSING: "processing",
  FROZEN: "frozen",
  BLOCKED: "frozen",
  EXPIRED: "frozen",
};

const KIND_MAP: Record<string, CardKind> = {
  VIRTUAL: "virtual",
  PHYSICAL: "physical",
};

const GRADIENTS = [
  "radial-gradient(120% 100% at 100% 0%, #2A1B6B 0%, #14113A 60%, #0A0B1F 100%)",
  "linear-gradient(135deg, #2C6BFF 0%, #1B4AD9 60%, #0F2FA0 100%)",
  "linear-gradient(135deg, #17362B 0%, #1F5A47 60%, #26A17B 100%)",
  "linear-gradient(135deg, #7A0A0A 0%, #B01818 60%, #EF3124 100%)",
  "linear-gradient(135deg, #4B1D66 0%, #6E2C90 60%, #A050D8 100%)",
];

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return GRADIENTS[hash % GRADIENTS.length];
}

// ---------- Мои карты ----------

interface BackendUserCard {
  id: string;
  provider: { id: string; code: string; name: string };
  offer_id: string | null;
  offer_image_url?: string | null;
  offer_image_url_dark?: string | null;
  brand: string;
  kind: string;
  name: string;
  cardholder_name: string;
  last_four: string;
  expiry: string;
  status: string;
  balance: string;
  currency: string;
  monthly_limit: string | null;
  used_this_month: string;
  issued_at: string | null;
}

function normalizeBrand(raw: string): CardBrand {
  return BRAND_MAP[raw.toLowerCase()] ?? "visa";
}
function normalizeKind(raw: string): CardKind {
  return KIND_MAP[raw.toUpperCase()] ?? "virtual";
}
function normalizeStatus(raw: string): CardStatus {
  return STATUS_MAP[raw.toUpperCase()] ?? "active";
}

function mapUserCard(c: BackendUserCard): UserCard {
  const currency = c.currency || "USD";
  const monthly = c.monthly_limit ? Number(c.monthly_limit) : undefined;
  const used = Number(c.used_this_month) || 0;
  return {
    id: c.id,
    provider: c.provider?.name || c.provider?.code || "Zero",
    brand: normalizeBrand(c.brand),
    kind: normalizeKind(c.kind),
    status: normalizeStatus(c.status),
    name: c.name,
    cardholderName: c.cardholder_name || "CARD HOLDER",
    fullNumber: "",
    lastFour: c.last_four || "••••",
    expiry: c.expiry || "—",
    cvv: "",
    balance: Number(c.balance) || 0,
    currency,
    limits: monthly ? { monthly, used, currency } : undefined,
    issuedAt: c.issued_at
      ? new Date(c.issued_at).toLocaleDateString("ru-RU")
      : undefined,
    imageUrl: c.offer_image_url ?? undefined,
    imageUrlDark: c.offer_image_url_dark ?? undefined,
    gradient: pickGradient(c.id + c.provider?.code),
  };
}

export async function getMyCards(): Promise<UserCard[]> {
  const res = await api.get<BackendUserCard[]>("/me/cards");
  return res.data.map(mapUserCard);
}

/**
 * Ответ на reveal реквизитов — discriminated union по `mode`:
 *
 * - `plain`  — Highnote / non-personalized: сам бэкенд возвращает PAN/CVV/expiry
 *   (расшифровал из БД или лениво дёрнул провайдера). UI рендерит текстовые поля.
 * - `iframe` — Airwallex personalized (PAN delegation): бэкенд возвращает URL
 *   secure-iframe провайдера. Фронт монтирует `<iframe src={iframe_url} />`,
 *   sensitive данные никогда не покидают PCI-контур Airwallex.
 */
export type CardSensitiveDetails =
  | {
      mode: "plain";
      pan: string;
      cvv: string;
      expiry: string;
      cardholder_name: string;
    }
  | {
      mode: "iframe";
      iframe_url: string;
      /** ISO-8601 или null, если провайдер TTL не сообщил. Обычно ~60 сек. */
      expires_at: string | null;
    };

/**
 * Реквизиты карты владельца.
 *
 * Требует **PIN** (тот же, что задавался при онбординге). PIN спрашиваем
 * на каждый reveal — кэша «валиден N минут» нет: показ PAN — самая
 * чувствительная операция, и один ввод на сессию слишком опасен.
 *
 * Только для карты в статусе `active`/`frozen` (для `processing`/`blocked`
 * бэкенд вернёт 400).
 */
export async function getCardDetails(
  cardId: string,
  pin: string,
): Promise<CardSensitiveDetails> {
  const res = await api.post<CardSensitiveDetails>(`/me/cards/${cardId}/details`, {
    pin,
  });
  return res.data;
}

// ---------- Офферы ----------

interface BackendOffer {
  id: string;
  code: string;
  name: string;
  brand: string;
  kind: string;
  price: string;
  currency: string;
  image_url?: string | null;
  image_url_dark?: string | null;
}
interface BackendProvider {
  id: string;
  code: string;
  name: string;
  country: string | null;
  offers: BackendOffer[];
}

function priceLabel(price: string, currency: string): string {
  const num = Number(price);
  if (!Number.isFinite(num)) return `${price} ${currency}`;
  if (currency === "USD" || currency === "USDT") return `$${num}`;
  if (currency === "RUB") return `${num.toLocaleString("ru-RU")} ₽`;
  if (currency === "EUR") return `€${num}`;
  return `${num} ${currency}`;
}

function offerFeatures(o: BackendOffer, providerCode: string): string[] {
  const kind = o.kind.toUpperCase();
  const feats: string[] = [];
  if (kind === "VIRTUAL") feats.push("Мгновенный онлайн-выпуск");
  if (kind === "PHYSICAL") feats.push("Физическая пластиковая карта");
  feats.push(`Валюта: ${o.currency}`);
  // Airwallex — принимаются в Visa-сети во всём мире.
  if (providerCode === "airwallex") {
    feats.push("Работает везде, где принимают Visa");
    feats.push("Без KYC — только email");
  }
  return feats;
}

function offerIssueTime(providerCode: string, kind: string): string {
  if (providerCode === "airwallex") return "Обычно 1–2 минуты";
  return kind.toUpperCase() === "VIRTUAL" ? "≈ 5 минут" : "5–10 дней";
}

export async function getCardOffers(): Promise<CardOffer[]> {
  const res = await api.get<BackendProvider[]>("/card-providers");
  const offers: CardOffer[] = [];
  for (const p of res.data) {
    for (const o of p.offers) {
      offers.push({
        id: o.id,
        provider: p.name,
        providerCode: p.code,
        providerCountry: p.country ?? undefined,
        name: o.name,
        description: `${p.name}: ${o.name} — карта для ежедневных расчётов и подписок.`,
        price: priceLabel(o.price, o.currency),
        priceComment: "разовый выпуск",
        issueTime: offerIssueTime(p.code, o.kind),
        features: offerFeatures(o, p.code),
        imageUrl: o.image_url ?? undefined,
        imageUrlDark: o.image_url_dark ?? undefined,
        gradient: pickGradient(o.id + p.code),
      });
    }
  }
  return offers;
}

// ---------- Выпуск карты ----------

export interface IssueCardRequest {
  offerId: string;
  cardholderName: string;
  dateOfBirth?: string; // YYYY-MM-DD
  email?: string;
  phone?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string; // ISO alpha-2
  };
}

export interface IssueCardResponse {
  id: string;
  brand: string;
  kind: string;
  name: string;
  cardholder_name: string;
  last_four: string;
  expiry: string;
  /**
   * `PROCESSING` — карта ушла в review (Airwallex — типично, `card_status=PENDING`).
   * `ACTIVE` — уже готова (Highnote test-env, Airwallex после активации через webhook).
   */
  status: string;
  currency: string;
  external_id: string | null;
  /**
   * Отдаётся один раз при выпуске (только в test-окружении Highnote или mock-режиме).
   * В prod PAN приходит на клиент через secure iframe, а не через наш API.
   * Airwallex PAN/CVV не отдаёт через API — только через embedded component.
   */
  pan?: string;
  cvv?: string;
}

export async function issueCard(payload: IssueCardRequest): Promise<IssueCardResponse> {
  const res = await api.post<IssueCardResponse>("/me/cards/issue", payload);
  return res.data;
}
